import React, { useRef, useEffect, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { StationService, StationInfo } from '../services/stationService';
import { supabase } from '../components/auth/AuthProvider';
import StationDataModal from '../components/stations/StationDataModal';
import StationControlPanel from '../components/stations/StationControlPanel';
import StationListModal from '../components/stations/StationListModal';
import BenchmarkModal from '../components/stations/BenchmarkModal';
import { filterStationsByRegion, RegionBounds } from '../utils/stationFilters';
import { benchmarkDatesService } from '../services/benchmarkDatesService';

const Stations: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const [stations, setStations] = useState<StationInfo[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedStation, setSelectedStation] = useState<StationInfo | null>(null);
  const [showDataModal, setShowDataModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionBounds | null>(null);
  const [showControlPanel, setShowControlPanel] = useState(true);
  const [showStationsList, setShowStationsList] = useState(false);
  const [showBenchmarks, setShowBenchmarks] = useState(false);
  const [isDrawingRegion, setIsDrawingRegion] = useState(false);

  // Filter stations based on selected region
  const filteredStations = useMemo(() => {
    return filterStationsByRegion(stations, selectedRegion);
  }, [stations, selectedRegion]);

  // Initialize and preload benchmark dates when stations are available
  const initializeBenchmarkDates = async () => {
    try {
      const getToken = async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          throw new Error('No authentication token available');
        }
        return token;
      };

      // Initialize the benchmark dates service
      benchmarkDatesService.initialize(getToken, import.meta.env.VITE_API_BASE_URL);

      // Preload dates in the background
      benchmarkDatesService.preloadAvailableDates().catch((err) => {
        console.warn('Failed to preload benchmark dates:', err);
      });
    } catch (err) {
      console.warn('Failed to initialize benchmark dates service:', err);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Set Mapbox access token
    mapboxgl.accessToken =
      'pk.eyJ1IjoibWFydmluZ2FibGVyIiwiYSI6ImNtODRpaW55NjBzdjcyanM3a2l3bDAxYXAifQ.3zrJdRYQt-lD-cP8phh1dw';

    // Initialize map with projection set to globe
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      projection: 'globe',
      zoom: 1.5,
      center: [0, 30],
      attributionControl: false,
      maxZoom: 8,
      minZoom: 0.5,
      performanceMetricsCollection: false,
    });

    map.on('style.load', () => {
      map.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });
    });

    map.on('load', () => {
      setMapLoaded(true);
    });

    mapRef.current = map;

    // Add global function for popup button clicks
    (window as any).openStationData = (
      stationId: string,
      name: string,
      lat: number,
      lng: number,
      elevation: number,
    ) => {
      setSelectedStation({ station: stationId, name, latitude: lat, longitude: lng, elevation });
      setShowDataModal(true);
      // Close any open popups
      const popups = document.getElementsByClassName('mapboxgl-popup');
      Array.from(popups).forEach((popup) => (popup as HTMLElement).remove());
    };

    return () => {
      // Clean up global function
      delete (window as any).openStationData;
      map.remove();
    };
  }, []);

  // Fetch stations data with caching
  useEffect(() => {
    const fetchStationsData = async () => {
      if (!mapLoaded) return;

      try {
        // Check cache controls
        const urlParams = new URLSearchParams(window.location.search);
        const disableCache =
          urlParams.get('disable-cache') === 'true' ||
          localStorage.getItem('disable-station-cache') === 'true';

        // Check cache first
        const cacheKey = 'jua-weather-synoptic-stations';
        const cacheTimeKey = 'jua-weather-synoptic-stations-time';
        const cached = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);

        // Cache for 24 hours (86400000 ms)
        const cacheExpiry = 24 * 60 * 60 * 1000;
        const now = Date.now();

        if (!disableCache && cached && cacheTime && now - parseInt(cacheTime) < cacheExpiry) {
          console.log('Loading stations from cache');
          const cachedStations = JSON.parse(cached);
          setStations(cachedStations);
          // Preload benchmark dates in the background
          initializeBenchmarkDates();
          return;
        }

        // Fetch from API if no cache or expired or disabled
        console.log(
          disableCache
            ? 'Fetching stations from API (cache disabled)'
            : 'Fetching stations from API',
        );
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const stationService = new StationService(import.meta.env.VITE_API_BASE_URL, getToken);

        // Fetch all stations
        const response = await stationService.getStations({
          recent_hours: 240,
        });
        if (response.error || !response.data) {
          console.warn('Failed to fetch stations:', response.error);
          return;
        }
        const stationsData = response.data.stations;
        setStations(stationsData);

        // Preload benchmark dates in the background
        initializeBenchmarkDates();

        // Cache the data unless disabled; guard against quota errors
        if (!disableCache) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify(stationsData));
            localStorage.setItem(cacheTimeKey, now.toString());
            console.log(`Cached ${stationsData.length} stations`);
          } catch {
            console.warn(
              `Failed to cache stations (${stationsData.length} stations): localStorage quota exceeded`,
            );
          }
        }
      } catch (err) {
        console.error('Error fetching stations data:', err);
      }
    };

    fetchStationsData();
  }, [mapLoaded]);

  // Handle region selection
  const handleRegionSelect = (bounds: RegionBounds | null) => {
    setSelectedRegion(bounds);
  };

  // Handle station selection from list
  const handleStationFromList = (station: StationInfo) => {
    setSelectedStation(station);
    setShowDataModal(true);
    setShowStationsList(false);

    // Fly to station on map
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [station.longitude, station.latitude],
        zoom: 6,
        duration: 2000,
      });
    }
  };

  // Add stations to map using GeoJSON (much more efficient)
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || filteredStations.length === 0) return;

    const map = mapRef.current;

    // Convert filtered stations to GeoJSON
    const geojson = {
      type: 'FeatureCollection' as const,
      features: filteredStations.map((station) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [station.longitude, station.latitude],
        },
        properties: {
          name: station.name,
          station: station.station,
          elevation: station.elevation,
          latitude: station.latitude,
          longitude: station.longitude,
        },
      })),
    };

    // Add source
    if (map.getSource('stations')) {
      (map.getSource('stations') as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      map.addSource('stations', {
        type: 'geojson',
        data: geojson,
      });

      // Add layer
      map.addLayer({
        id: 'stations',
        type: 'circle',
        source: 'stations',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 2, 3, 4, 5, 6, 7],
          'circle-color': '#E34290',
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8,
        },
      });

      // Add click event for popups
      map.on('click', 'stations', (e) => {
        // Don't show popups when drawing regions
        if (isDrawingRegion) return;

        if (e.features && e.features[0]) {
          const feature = e.features[0];
          const coordinates = (feature.geometry as any).coordinates.slice();
          const props = feature.properties;

          // Ensure that if the map is zoomed out such that multiple
          // copies of the feature are visible, the popup appears
          // over the copy being pointed to.
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: true,
            maxWidth: '300px',
          })
            .setLngLat(coordinates)
            .setHTML(
              `
                                <div style="
                   padding: 16px; 
                   font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                   background: #F5E6D3;
                   color: #2d3748;
                   border-radius: 12px;
                   box-shadow: 0 10px 25px rgba(0,0,0,0.3);
                   min-width: 250px;
                   border: 1px solid rgba(0,0,0,0.1);
                 ">
                 <div style="
                   font-size: 18px; 
                   font-weight: 700; 
                   margin-bottom: 12px; 
                   text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                   line-height: 1.3;
                 ">
                   📍 ${props?.name}
                 </div>
                 
                                    <div style="
                     background: rgba(0,0,0,0.05);
                     padding: 12px;
                     border-radius: 8px;
                     margin-bottom: 12px;
                     backdrop-filter: blur(10px);
                   ">
                   <div style="
                     font-size: 13px; 
                     margin-bottom: 8px;
                     display: flex;
                     align-items: center;
                     gap: 8px;
                   ">
                     <span style="opacity: 0.9;">🆔</span>
                     <span style="font-weight: 500;">Station ID:</span>
                                            <span style="font-family: 'Monaco', 'Consolas', monospace; background: rgba(0,0,0,0.1); padding: 2px 6px; border-radius: 4px; font-size: 12px;">
                       ${props?.station}
                     </span>
                   </div>
                   
                   <div style="
                     font-size: 13px; 
                     margin-bottom: 8px;
                     display: flex;
                     align-items: center;
                     gap: 8px;
                   ">
                     <span style="opacity: 0.9;">🌍</span>
                     <span style="font-weight: 500;">Location:</span>
                     <span>${props?.latitude?.toFixed(3)}°, ${props?.longitude?.toFixed(3)}°</span>
                   </div>
                   
                   <div style="
                     font-size: 13px;
                     display: flex;
                     align-items: center;
                     gap: 8px;
                   ">
                     <span style="opacity: 0.9;">⛰️</span>
                     <span style="font-weight: 500;">Elevation:</span>
                     <span style="font-weight: 600;">${props?.elevation?.toFixed(0)}m</span>
                                        </div>
                   </div>
                   
                   <div style="margin-top: 12px; text-align: center;">
                     <button 
                       onclick="window.openStationData('${props?.station}', '${props?.name}', ${props?.latitude}, ${props?.longitude}, ${props?.elevation})"
                       style="
                         background: #E34290;
                         color: white;
                         border: none;
                         padding: 8px 16px;
                         border-radius: 6px;
                         font-size: 12px;
                         font-weight: 600;
                         cursor: pointer;
                         transition: all 0.2s ease;
                       "
                       onmouseover="this.style.background='#c73678'"
                       onmouseout="this.style.background='#E34290'"
                     >
                       📊 View Data
                     </button>
                   </div>
                 </div>
             `,
            )
            .addTo(map);
        }
      });

      // Change cursor on hover
      map.on('mouseenter', 'stations', () => {
        if (!isDrawingRegion) {
          map.getCanvas().style.cursor = 'pointer';
        }
      });

      map.on('mouseleave', 'stations', () => {
        if (!isDrawingRegion) {
          map.getCanvas().style.cursor = '';
        }
      });
    }
  }, [mapLoaded, filteredStations, isDrawingRegion]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="absolute inset-0">
        <div
          ref={mapContainerRef}
          style={{
            height: '100%',
            width: '100%',
          }}
        />
      </div>

      {/* Control Panel */}
      <StationControlPanel
        isExpanded={showControlPanel}
        onToggle={() => setShowControlPanel(!showControlPanel)}
        map={mapRef.current}
        selectedRegion={selectedRegion}
        onRegionSelect={handleRegionSelect}
        filteredStations={filteredStations}
        totalStations={stations.length}
        onShowStationsList={() => setShowStationsList(true)}
        onShowBenchmarks={() => setShowBenchmarks(true)}
        onDrawingStateChange={setIsDrawingRegion}
        isMobile={window.innerWidth < 768}
      />

      {/* Station Data Modal */}
      <StationDataModal
        station={selectedStation}
        isOpen={showDataModal}
        onClose={() => {
          setShowDataModal(false);
          setSelectedStation(null);
        }}
      />

      {/* Station List Modal */}
      <StationListModal
        isOpen={showStationsList}
        onClose={() => setShowStationsList(false)}
        stations={filteredStations}
        selectedRegion={selectedRegion}
        onStationSelect={handleStationFromList}
      />

      {/* Benchmark Modal */}
      <BenchmarkModal
        isOpen={showBenchmarks}
        onClose={() => setShowBenchmarks(false)}
        stations={filteredStations}
        selectedRegion={selectedRegion}
      />
    </div>
  );
};

export default Stations;
